const HOUR = 60 * 60 * 1000
const HALF_HOUR = 30 * 60 * 1000

export const DATE_ERROR_REASONS = Object.freeze({
  INVALID_SEQUENCE: 'invalid_sequence',
  INSUFFICIENT_NOTICE: 'insufficient_notice',
  MISSING_DATE: 'missing_date',
  PAST_DATE: 'past_date',
  WAREHOUSE_HOURS: 'warehouse_hours',
})

const asDate = value => {
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

const fieldLabel = mode => ({
  pickup: 'warehouse pickup',
  return: 'warehouse return',
  delivery: 'delivery and setup arrival',
  collection: 'equipment collection',
}[mode] || 'date and time')

const issue = (reason, message) => ({ message, reason })

export const dateIssueMessages = issues => Object.fromEntries(
  Object.entries(issues).map(([key, value]) => [key, value?.message || ''])
)

export const toDateTimeLocalValue = value => {
  const date = asDate(value)
  if (!date) return undefined
  const pad = number => String(number).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

export const getWarehouseDateIssue = ({ mode, pickupValue, value, now = new Date() }) => {
  const selected = asDate(value)
  const label = fieldLabel(mode)
  if (!selected) return issue(DATE_ERROR_REASONS.MISSING_DATE, `Choose the ${label} date and time.`)
  if (selected.getTime() < now.getTime()) {
    return issue(DATE_ERROR_REASONS.PAST_DATE, `Choose a ${label} date and time in the future.`)
  }

  const minutes = selected.getHours() * 60 + selected.getMinutes()
  if ([0, 6].includes(selected.getDay())) {
    if (minutes < 9 * 60 || minutes > 12 * 60) {
      return issue(DATE_ERROR_REASONS.WAREHOUSE_HOURS, `Weekend ${label} is available from 9:00 AM to 12:00 PM.`)
    }
  } else {
    const closingMinutes = mode === 'return' ? 16 * 60 : 17 * 60
    if (minutes < 9 * 60 || minutes > closingMinutes) {
      return issue(DATE_ERROR_REASONS.WAREHOUSE_HOURS, mode === 'return'
        ? 'Choose a weekday warehouse return between 9:00 AM and 4:00 PM.'
        : 'Choose a weekday warehouse pickup between 9:00 AM and 5:00 PM.')
    }
  }

  const pickup = asDate(pickupValue)
  if (mode === 'return' && pickup && pickup.getTime() + HOUR > selected.getTime()) {
    return issue(DATE_ERROR_REASONS.INVALID_SEQUENCE, 'Choose a return time at least 1 hour after warehouse pickup.')
  }
  return null
}

export const getWarehouseDateError = options => getWarehouseDateIssue(options)?.message || ''

export const getWarehouseScheduleIssues = ({ pickupValue, returnValue, now = new Date() }) => ({
  pickup: getWarehouseDateIssue({ mode: 'pickup', value: pickupValue, pickupValue, now }),
  return: getWarehouseDateIssue({ mode: 'return', value: returnValue, pickupValue, now }),
})

export const getWarehouseScheduleErrors = options => dateIssueMessages(getWarehouseScheduleIssues(options))

const nextValidWarehouseDate = ({ mode, pickupValue, startValue, now }) => {
  let candidate = new Date(Math.ceil(new Date(startValue).getTime() / HALF_HOUR) * HALF_HOUR)
  const searchLimit = candidate.getTime() + 8 * 24 * HOUR

  while (candidate.getTime() <= searchLimit) {
    if (!getWarehouseDateIssue({ mode, pickupValue, value: candidate, now })) return candidate
    candidate = new Date(candidate.getTime() + HALF_HOUR)
  }

  return null
}

export const defaultWarehouseSchedule = (nowValue = new Date()) => {
  const now = asDate(nowValue)
  if (!now) return { pickup: null, returnDate: null }

  const pickup = nextValidWarehouseDate({
    mode: 'pickup',
    pickupValue: null,
    startValue: new Date(now.getTime() + HALF_HOUR),
    now,
  })
  const returnDate = pickup
    ? nextValidWarehouseDate({
        mode: 'return',
        pickupValue: pickup,
        startValue: new Date(pickup.getTime() + 24 * HOUR),
        now,
      })
    : null

  return { pickup, returnDate }
}

export const getDeliveryDateIssue = ({ deliveryValue, enoughTime, mode, value, now = new Date() }) => {
  const selected = asDate(value)
  const label = fieldLabel(mode)
  if (!selected) return issue(DATE_ERROR_REASONS.MISSING_DATE, `Choose the ${label} date and time.`)
  if (selected.getTime() < now.getTime()) {
    return issue(DATE_ERROR_REASONS.PAST_DATE, `Choose a ${label} date and time in the future.`)
  }
  if (mode === 'delivery' && !enoughTime(value, now)) {
    return issue(DATE_ERROR_REASONS.INSUFFICIENT_NOTICE, 'This time is not available for online booking. Choose a later time, or call us for rush delivery.')
  }

  const delivery = asDate(deliveryValue)
  if (mode === 'collection' && delivery && selected.getTime() < delivery.getTime()) {
    return issue(DATE_ERROR_REASONS.INVALID_SEQUENCE, 'Choose an equipment collection time after the delivery and setup arrival.')
  }
  return null
}

export const getDeliveryDateError = options => getDeliveryDateIssue(options)?.message || ''

export const getDeliveryScheduleIssues = ({ collectionValue, deliveryValue, enoughTime, now = new Date() }) => ({
  delivery: getDeliveryDateIssue({ mode: 'delivery', value: deliveryValue, deliveryValue, enoughTime, now }),
  pickup: getDeliveryDateIssue({ mode: 'collection', value: collectionValue, deliveryValue, enoughTime, now }),
})

export const getDeliveryScheduleErrors = options => dateIssueMessages(getDeliveryScheduleIssues(options))

export const firstDateError = (errors, order) => order.find(key => Boolean(errors[key]))
